import {
  documentEvidenceTypes,
  isCalendarDate,
  type CreateDocumentEvidenceRequest,
  type DocumentEvidenceType,
  type DocumentEvidenceView,
  type EvidenceVerificationStatus,
  type RejectDocumentEvidenceRequest,
  type UpdateDocumentEvidenceRequest,
  type VerifyDocumentEvidenceRequest,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const evidenceTypeKeys: Readonly<Record<DocumentEvidenceType, TranslationKey>> = {
  medical_clearance: 'evidence.type.medical_clearance',
  under_18_agreement: 'evidence.type.under_18_agreement',
  promotion_agreement: 'evidence.type.promotion_agreement',
  loan_agreement: 'evidence.type.loan_agreement',
  transfer_approval: 'evidence.type.transfer_approval',
};

export const evidenceStatusKeys: Readonly<Record<EvidenceVerificationStatus, TranslationKey>> = {
  pending: 'evidence.status.pending',
  verified: 'evidence.status.verified',
  rejected: 'evidence.status.rejected',
};

/** The translated label of a known type; a stored type this release does not know is shown by its code. */
export function evidenceTypeLabelKey(documentType: string): TranslationKey | null {
  const known = documentEvidenceTypes.find((candidate) => candidate === documentType);
  return known === undefined ? null : evidenceTypeKeys[known];
}

export interface EvidenceForm {
  athleteId: string;
  documentType: DocumentEvidenceType;
  issuer: string;
  reference: string;
  validFrom: string;
  validTo: string;
}

export const emptyEvidenceForm: EvidenceForm = {
  athleteId: '',
  documentType: 'medical_clearance',
  issuer: '',
  reference: '',
  validFrom: '',
  validTo: '',
};

function textOrNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

/** The create request, or null while the form is incomplete or its dates contradict each other. */
export function evidenceRequest(form: EvidenceForm): CreateDocumentEvidenceRequest | null {
  if (form.athleteId === '' || !isCalendarDate(form.validFrom)) return null;
  if (form.validTo !== '') {
    if (!isCalendarDate(form.validTo) || form.validTo < form.validFrom) return null;
  }
  return {
    athleteId: form.athleteId,
    documentType: form.documentType,
    issuer: textOrNull(form.issuer),
    reference: textOrNull(form.reference),
    validFrom: form.validFrom,
    validTo: form.validTo === '' ? null : form.validTo,
  };
}

export interface EvidenceCorrectionForm {
  issuer: string;
  reference: string;
  validFrom: string;
  validTo: string;
}

export function evidenceCorrectionFormOf(evidence: DocumentEvidenceView): EvidenceCorrectionForm {
  return {
    issuer: evidence.issuer ?? '',
    reference: evidence.reference ?? '',
    validFrom: evidence.validFrom,
    validTo: evidence.validTo ?? '',
  };
}

/**
 * Only the fields that differ from the record are sent. Null when nothing changed or a day is
 * invalid, so the save button stays off.
 */
export function evidenceCorrectionRequest(
  form: EvidenceCorrectionForm,
  evidence: DocumentEvidenceView,
): UpdateDocumentEvidenceRequest | null {
  if (!isCalendarDate(form.validFrom)) return null;
  const validTo = form.validTo === '' ? null : form.validTo;
  if (validTo !== null && (!isCalendarDate(validTo) || validTo < form.validFrom)) return null;
  const request: UpdateDocumentEvidenceRequest = {};
  const issuer = textOrNull(form.issuer);
  if (issuer !== evidence.issuer) request.issuer = issuer;
  const reference = textOrNull(form.reference);
  if (reference !== evidence.reference) request.reference = reference;
  if (form.validFrom !== evidence.validFrom) request.validFrom = form.validFrom;
  if (validTo !== evidence.validTo) request.validTo = validTo;
  return Object.keys(request).length === 0 ? null : request;
}

export function verifyRequest(note: string): VerifyDocumentEvidenceRequest {
  return { note: textOrNull(note) };
}

/** The rejection (or withdrawal) request, or null while no reason is written. */
export function rejectRequest(reason: string): RejectDocumentEvidenceRequest | null {
  const trimmed = reason.trim();
  return trimmed === '' ? null : { reason: trimmed };
}

const statusOrder: Readonly<Record<EvidenceVerificationStatus, number>> = {
  pending: 0,
  verified: 1,
  rejected: 2,
};

/** Records waiting for a decision first, then verified, then rejected; the server's order within each group. */
export function pendingFirst(evidence: readonly DocumentEvidenceView[]): DocumentEvidenceView[] {
  return evidence
    .map((record, position) => ({ record, position }))
    .sort(
      (left, right) =>
        statusOrder[left.record.verificationStatus] -
          statusOrder[right.record.verificationStatus] || left.position - right.position,
    )
    .map(({ record }) => record);
}

export function evidencePath(
  base: string,
  evidenceId?: string,
  action?: 'verify' | 'reject',
): string {
  const root = `${base}/document-evidence`;
  if (evidenceId === undefined) return root;
  const one = `${root}/${encodeURIComponent(evidenceId)}`;
  return action === undefined ? one : `${one}/${action}`;
}
